import React from 'react';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Tooltip, Popconfirm, message } from 'antd';
import { useToggleSourceFieldMutation, useDeleteDataSourceMutation } from '../../redux/api/compassPortal';

const formatDate = (ts) => {
    if (!ts) return '';
    try {
        const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
};

const CircleIndicator = ({ active, date, onClick, disabled, blocked, tooltipText }) => (
    <Tooltip title={tooltipText || (disabled ? 'Admin only' : active ? 'Click to unmark' : 'Click to mark as collected')}>
        <div className="cp-circle-indicator">
            <div
                className={`cp-circle ${active ? 'cp-circle--collected' : ''} ${blocked ? 'cp-circle--blocked' : ''}`}
                onClick={!disabled ? onClick : undefined}
                style={disabled ? { cursor: 'default', opacity: active ? 1 : 0.5 } : {}}
            />
            {active && date && <span className="cp-circle__date">{formatDate(date)}</span>}
        </div>
    </Tooltip>
);

const MetadataTags = ({ metadata }) => {
    if (!metadata || Object.keys(metadata).length === 0) return null;
    // Show up to 3 key metadata values as small tags
    const display = ['region', 'platform', 'data_stage', 'market'];
    const entries = display
        .filter((k) => metadata[k])
        .map((k) => metadata[k]);
    if (entries.length === 0) return null;
    return (
        <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
            {entries.map((val, i) => (
                <span key={i} style={{
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 3,
                    background: 'rgba(1, 255, 133, 0.08)',
                    color: 'var(--green-color)',
                    border: '1px solid rgba(1, 255, 133, 0.15)',
                }}>{val}</span>
            ))}
        </div>
    );
};

const DataSourceTable = ({
    sources,
    groups,
    projectId,
    mode,
    selectedGroupId,
    onEditSource,
    onViewSource,
    onRaiseIssue,
    onUploadProcessed,
    onDownloadOriginal,
    onDownloadProcessed,
    isAdmin,
    isInternal,
}) => {
    const [toggleField] = useToggleSourceFieldMutation();
    const [deleteSource] = useDeleteDataSourceMutation();

    const groupMap = {};
    (groups || []).forEach((g) => { groupMap[g.group_id] = g.name; });

    let filteredSources = sources || [];
    if (selectedGroupId) {
        filteredSources = filteredSources.filter((s) => s.group_id === selectedGroupId);
    }
    if (mode === 'processing') {
        filteredSources = filteredSources.filter((s) => s.collected);
    }

    // Group sources by group_id for row spanning
    const grouped = {};
    filteredSources.forEach((s) => {
        if (!grouped[s.group_id]) grouped[s.group_id] = [];
        grouped[s.group_id].push(s);
    });
    const groupOrder = (groups || []).map(g => g.group_id).filter(id => grouped[id]);

    if (filteredSources.length === 0) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--medium-light-grey-color)' }}>
                {mode === 'processing' ? 'No collected sources to process' : 'No data sources yet'}
            </div>
        );
    }

    return (
        <table className="cp-table">
            <thead>
                <tr>
                    <th>Group</th>
                    <th>Source</th>
                    {mode === 'collection' && <th style={{ textAlign: 'center' }}>Collected</th>}
                    {mode === 'processing' && <th style={{ textAlign: 'center' }}>Blocked</th>}
                    {mode === 'processing' && <th style={{ textAlign: 'center' }}>Processed</th>}
                    <th style={{ textAlign: 'center' }}>Flags</th>
                    <th style={{ textAlign: 'center' }}>Info</th>
                    {mode === 'collection' && <th>Uploaded By</th>}
                    {mode === 'processing' && <th style={{ textAlign: 'center' }}>Issue</th>}
                    {mode === 'processing' && <th>Processed By</th>}
                    {mode === 'processing' && <th style={{ textAlign: 'center' }}>File</th>}
                    {mode === 'collection' && <th></th>}
                </tr>
            </thead>
            <tbody>
                {groupOrder.map((groupId) => {
                    const groupSources = grouped[groupId];
                    return groupSources.map((source, idx) => {
                        const isBlocked = source.processing_issue_status === 'open';
                        return (
                            <tr key={source.source_id}>
                                {idx === 0 ? (
                                    <td className="cp-table__group-cell" rowSpan={groupSources.length}>
                                        {groupMap[groupId] || groupId}
                                    </td>
                                ) : null}
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        {source.name}
                                        {!source.file_url && mode === 'collection' && (
                                            <span style={{ fontSize: 11, color: 'var(--medium-light-grey-color)', background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: 4 }}>
                                                No file
                                            </span>
                                        )}
                                        {source.processing_issue_status === 'open' && mode === 'collection' && (
                                            <Tooltip title={source.processing_issue || 'Click to view issue and respond'}>
                                                <button
                                                    className="cp-issue-badge"
                                                    onClick={() => onRaiseIssue?.(source)}
                                                >
                                                    <WarningOutlined /> Issue Raised — Action Needed
                                                </button>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <MetadataTags metadata={source.metadata} />
                                </td>

                                {mode === 'collection' && (
                                    <td style={{ textAlign: 'center' }}>
                                        <CircleIndicator
                                            active={source.collected}
                                            date={source.collected_date}
                                            onClick={() => {
                                                toggleField({ projectId, sourceId: source.source_id, field: 'collected' });
                                                message[source.collected ? 'info' : 'success'](
                                                    source.collected ? 'Source unmarked — it will need to be collected before processing can begin' : 'Source marked as collected — it will now be queued for processing'
                                                );
                                            }}
                                            tooltipText={source.collected ? 'Click to unmark' : 'Click to mark as collected'}
                                        />
                                    </td>
                                )}

                                {mode === 'processing' && (
                                    <td style={{ textAlign: 'center' }}>
                                        {isBlocked ? (
                                            <div className="cp-circle-indicator">
                                                <div className="cp-circle cp-circle--blocked" />
                                            </div>
                                        ) : (
                                            <div className="cp-circle-indicator">
                                                <div className="cp-circle" style={{ borderColor: 'transparent' }} />
                                            </div>
                                        )}
                                    </td>
                                )}

                                {mode === 'processing' && (
                                    <td style={{ textAlign: 'center' }}>
                                        <CircleIndicator
                                            active={!!source.processed_file_url}
                                            date={source.processed_date}
                                            disabled
                                            tooltipText={source.processed_file_url ? 'Processed file uploaded' : 'Upload a processed file to mark as processed'}
                                        />
                                    </td>
                                )}

                                <td style={{ textAlign: 'center' }}>
                                    {source.flags ? <WarningOutlined className="cp-flag" /> : null}
                                </td>

                                <td style={{ textAlign: 'center' }}>
                                    <button className="cp-info-btn" onClick={() => onViewSource?.(source)}>
                                        <InfoCircleOutlined />
                                    </button>
                                </td>

                                {mode === 'collection' && (
                                    <td>{source.collected_by || '—'}</td>
                                )}

                                {mode === 'processing' && (
                                    <td style={{ textAlign: 'center' }}>
                                        {isBlocked ? (
                                            <Tooltip title={source.processing_issue || 'View issue details'}>
                                                <button
                                                    className="cp-table-btn cp-table-btn--open"
                                                    onClick={() => onRaiseIssue?.(source)}
                                                >
                                                    &#9650; Open
                                                </button>
                                            </Tooltip>
                                        ) : (
                                            isInternal && (
                                                <button
                                                    className="cp-table-btn cp-table-btn--raise"
                                                    onClick={() => onRaiseIssue?.(source)}
                                                >
                                                    + Raise
                                                </button>
                                            )
                                        )}
                                    </td>
                                )}

                                {mode === 'processing' && (
                                    <td>{source.processed_by || '—'}</td>
                                )}

                                {mode === 'processing' && (
                                    <td>
                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}>
                                            {source.file_url && (
                                                <button className="cp-table-btn cp-table-btn--edit" onClick={() => onDownloadOriginal?.(source)}>
                                                    &#8615; Original
                                                </button>
                                            )}
                                            {isInternal && (
                                                <button className="cp-table-btn cp-table-btn--edit" onClick={() => onUploadProcessed?.(source)}>
                                                    &#8613; Processed
                                                </button>
                                            )}
                                            {source.processed_file_url && (
                                                <button className="cp-table-btn cp-table-btn--edit" onClick={() => onDownloadProcessed?.(source)}>
                                                    &#8615; Processed
                                                </button>
                                            )}
                                            {source.processed_file_url && (
                                                <span className="cp-processed-badge">&#10003;</span>
                                            )}
                                        </div>
                                    </td>
                                )}

                                {mode === 'collection' && (
                                    <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                                    <button className="cp-table-btn cp-table-btn--edit" onClick={() => onEditSource?.(source)}>
                                                        &#9998; Edit
                                                    </button>
                                            {isAdmin && (
                                                <>
                                                    <Popconfirm
                                                        title={`Delete "${source.name}"?`}
                                                        description="This cannot be undone."
                                                        okText="Delete"
                                                        okType="danger"
                                                        onConfirm={() => {
                                                            deleteSource({ projectId, sourceId: source.source_id });
                                                            message.success(`Deleted "${source.name}"`);
                                                        }}
                                                    >
                                                        <button className="cp-table-btn cp-table-btn--delete">
                                                            &#128465; Delete
                                                        </button>
                                                    </Popconfirm>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        );
                    });
                })}
            </tbody>
        </table>
    );
};

export default DataSourceTable;
